import * as yup from 'yup';
import { CashDocumentType } from '../Types/cashDocumentTypes';

/**
 * Validation schema for creating a cash document (KP/KW)
 */
export const createCashDocumentSchema = yup.object({
  // Document type (required)
  type: yup
    .mixed<CashDocumentType>()
    .oneOf([CashDocumentType.KP, CashDocumentType.KW], 'Wybierz typ dokumentu')
    .required('Typ dokumentu jest wymagany'),

  // Amount (required, must be positive)
  amount: yup
    .number()
    .required('Kwota jest wymagana')
    .positive('Kwota musi być większa od zera')
    .max(999999999.99, 'Kwota nie może przekraczać 999 999 999,99'),

  // Currency (required, ISO 4217 - 3 letters)
  currency: yup
    .string()
    .required('Waluta jest wymagana')
    .length(3, 'Waluta musi mieć 3 znaki (np. PLN, EUR, USD)')
    .matches(/^[A-Z]{3}$/, 'Waluta musi składać się z 3 wielkich liter (np. PLN)'),

  // Document date (required, YYYY-MM-DD format)
  documentDate: yup
    .string()
    .required('Data dokumentu jest wymagana')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Nieprawidłowy format daty (YYYY-MM-DD)'),

  // Person name (required for KP/KW - who received/disbursed cash)
  personName: yup
    .string()
    .required('Imię i nazwisko jest wymagane')
    .min(2, 'Imię i nazwisko musi mieć min. 2 znaki')
    .max(200, 'Imię i nazwisko może mieć max. 200 znaków'),

  // Purpose of payment (required)
  purpose: yup
    .string()
    .required('Cel płatności jest wymagany')
    .min(3, 'Cel płatności musi mieć min. 3 znaki')
    .max(500, 'Cel płatności może mieć max. 500 znaków'),

  // Additional note (optional)
  note: yup.string().nullable().max(1000, 'Notatka może mieć max. 1000 znaków'),

  // Reservation ID (optional - link to reservation)
  reservationId: yup
    .number()
    .nullable()
    .positive('ID rezerwacji musi być liczbą dodatnią')
    .integer('ID rezerwacji musi być liczbą całkowitą'),

  // Invoice ID (optional - link to invoice for auto-payment)
  invoiceId: yup
    .number()
    .nullable()
    .positive('ID faktury musi być liczbą dodatnią')
    .integer('ID faktury musi być liczbą całkowitą'),

  // Issued by (optional - who created the document)
  issuedBy: yup.string().nullable().max(200, 'Wystawił może mieć max. 200 znaków'),
});

/**
 * Validation schema for updating a cash document
 * (Same as create, but allows partial updates)
 */
export const updateCashDocumentSchema = yup.object({
  // Document type (optional for update)
  type: yup
    .mixed<CashDocumentType>()
    .oneOf([CashDocumentType.KP, CashDocumentType.KW], 'Wybierz typ dokumentu')
    .optional(),

  // Amount (optional for update, but must be positive if provided)
  amount: yup
    .number()
    .optional()
    .positive('Kwota musi być większa od zera')
    .max(999999999.99, 'Kwota nie może przekraczać 999 999 999,99'),

  // Currency (optional for update)
  currency: yup
    .string()
    .optional()
    .length(3, 'Waluta musi mieć 3 znaki (np. PLN, EUR, USD)')
    .matches(/^[A-Z]{3}$/, 'Waluta musi składać się z 3 wielkich liter (np. PLN)'),

  // Document date (optional for update)
  documentDate: yup
    .string()
    .optional()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Nieprawidłowy format daty (YYYY-MM-DD)'),

  // Person name (optional for update)
  personName: yup
    .string()
    .optional()
    .min(2, 'Imię i nazwisko musi mieć min. 2 znaki')
    .max(200, 'Imię i nazwisko może mieć max. 200 znaków'),

  // Purpose of payment (optional for update)
  purpose: yup
    .string()
    .optional()
    .min(3, 'Cel płatności musi mieć min. 3 znaki')
    .max(500, 'Cel płatności może mieć max. 500 znaków'),

  // Additional note (optional)
  note: yup.string().nullable().max(1000, 'Notatka może mieć max. 1000 znaków'),

  // Reservation ID (optional)
  reservationId: yup
    .number()
    .nullable()
    .positive('ID rezerwacji musi być liczbą dodatnią')
    .integer('ID rezerwacji musi być liczbą całkowitą'),

  // Invoice ID (optional)
  invoiceId: yup
    .number()
    .nullable()
    .positive('ID faktury musi być liczbą dodatnią')
    .integer('ID faktury musi być liczbą całkowitą'),

  // Issued by (optional)
  issuedBy: yup.string().nullable().max(200, 'Wystawił może mieć max. 200 znaków'),
});

/**
 * Validation schema for cash balance query filters
 */
export const cashBalanceFiltersSchema = yup.object({
  // Date from (required for balance query)
  from: yup
    .string()
    .required('Data początkowa jest wymagana')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Nieprawidłowy format daty (YYYY-MM-DD)'),

  // Date to (required for balance query)
  to: yup
    .string()
    .required('Data końcowa jest wymagana')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Nieprawidłowy format daty (YYYY-MM-DD)')
    .test('date-order', 'Data końcowa nie może być wcześniejsza niż początkowa', function (value) {
      const { from } = this.parent;
      if (!from || !value) return true;
      return new Date(value) >= new Date(from);
    }),

  // Currency filter (optional, default PLN)
  currency: yup
    .string()
    .optional()
    .length(3, 'Waluta musi mieć 3 znaki')
    .matches(/^[A-Z]{3}$/, 'Waluta musi składać się z 3 wielkich liter'),
});

/**
 * Export type inference for React Hook Form
 */
export type CreateCashDocumentFormData = yup.InferType<typeof createCashDocumentSchema>;
export type UpdateCashDocumentFormData = yup.InferType<typeof updateCashDocumentSchema>;
export type CashBalanceFiltersFormData = yup.InferType<typeof cashBalanceFiltersSchema>;
